import {observable} from 'mobx';
import {evite} from 'evite';
import {useLocalStore} from 'mobx-react';
import {createContext, useContext} from 'react';
import {EventFormStore} from './event_form_store';
import {EventGuessingGamesStore} from './guessing_games_store';
import {TemplateStore} from './TemplateStore';
import {WidgetStore} from './widget_store';
import {AddGuestStore} from './guest_store';
import {ContactStore} from './contact_store';
import {ChangeDesignStore} from './ChangeDesignStore';
import {PaymentStore} from './payment_store';
import {VideoStore} from './video_store';

export function createStore({event, template, changeDesign} = {}) {
  const creationStore = {};
  const designStep = {};

  creationStore.templateStore = new TemplateStore(creationStore, template, designStep);

  creationStore.videoStore = new VideoStore(event.event_videos_id);
  creationStore.widgetStore = new WidgetStore({...window.event_widgets});

  event.is_invite = window.is_invite;
  _initializeEventOffers(event);

  creationStore.event = observable({...event});

  creationStore.event.get = function (str, opt_defaultValue) {
    return evite.get(creationStore.event, str, opt_defaultValue);
  };

  creationStore.event.set = function (str, value) {
    evite.set(creationStore.event, str, value, {strict: true});
  };

  creationStore.event.isValidEvent = function () {
    return true;
  };

  creationStore.event.hasBeenSent = () =>
    // more like "should be considered sent" - scheduled send events
    // (probably cards) should be considered sent re: "Update" vs.
    // "Continue" etc.
    creationStore.event.status === 'sending' || creationStore.event.status === 'scheduled';

  creationStore.changeDesignStore = {enabled: false};

  if (!evite.detects.isMobile) {
    creationStore.changeDesignStore = new ChangeDesignStore({
      store: creationStore,
      ...changeDesign,
    });
  }

  // Removed conditional after tests added for guestStore / appConfig, eventForm
  if (process.env.NODE_ENV !== 'test') {
    creationStore.guestStore = new AddGuestStore(creationStore.event);

    creationStore.contactStore = new ContactStore(creationStore.event, 'contacts');
    creationStore.guestStore.registerContactStore(creationStore.contactStore);

    creationStore.importContactStore = new ContactStore(creationStore.event, 'import');
    creationStore.guestStore.registerContactStore(creationStore.importContactStore);

    creationStore.guessingGames = new EventGuessingGamesStore(
      creationStore.event,
      window.guessing_game_config_data,
      window.available_guessing_games_by_event_type,
      window.existing_game_instances
    );

    creationStore.eventForm = new EventFormStore(
      creationStore.event,
      creationStore.templateStore.template,
      creationStore.widgetStore
    );

    // add EventGuessingGamesStore's validateGameInstances() method to EventFormStore's "external validators"
    creationStore.eventForm.addExternalValidator(creationStore.guessingGames.validateGameInstances);

    // also add the guessing games store to the event form store
    // as an "external provider"
    creationStore.eventForm.addExternalDataContributor(
      creationStore.guessingGames.addToEventSubmissionData,
      creationStore.guessingGames.handleEventSubmissionResponse
    );
  }

  creationStore.paymentStore = new PaymentStore(
    window.paymentCalcSummary,
    true,
    creationStore.event,
    creationStore.guestStore
  );

  return creationStore;
}

const _initializeEventOffers = (event) => {
  // need all offer keys to be present in event.options.offers for updating
  // to work correctly
  // first collect all the available offer keys
  const availableOfferKeys = [];
  if (
    window.paymentCalcSummary &&
    window.paymentCalcSummary.availableOffers &&
    window.paymentCalcSummary.availableOffers.length > 0
  ) {
    window.paymentCalcSummary.availableOffers.forEach((offerObj) => {
      availableOfferKeys.push(offerObj.offerKey);
    });
  }

  // now zero out offer keys for any that aren't present
  if (event && event.options) {
    availableOfferKeys.forEach((offerKey) => {
      if (!(offerKey in event.options.offers)) {
        event.options.offers[offerKey] = 0;
      }
    });
  }
};

let _creationStore;
// Stand alone doesn't have an initial event so we don't need the create store
if (process.env.NODE_ENV !== 'test' && !!window.initial_evite_event) {
  if (window.creationStore) {
    throw new Error(
      'Unexpected copy of window.creationStore module exported, you might have used an unusual import instead of the "create-store" alias.'
    );
  }
  _creationStore = createStore({
    event: window.initial_evite_event || {},
    template: (evite.constants && evite.constants.TEMPLATE) || {},
    widget: window.event_widgets || {},
    category: {},
    categories: [],
    changeDesign: {enabled: false},
  });

  evite.provideStores(_creationStore);

  if (!window.creationStoreExports) {
    window.creationStoreExports = exports;
  } else {
    throw new Error(
      'Unexpected copy of creationStoreExports module exported, you might have used an unusual import instead of using `@inject` or `evite.injectStores`.'
    );
  }
}
const creationStore = _creationStore;
const storesContext = createContext();

const StoreProvider = ({children}) => {
  const store = useLocalStore(() => creationStore);
  return <storesContext.Provider value={store}>{children}</storesContext.Provider>;
};

const useStore = () => {
  const store = useContext(storesContext);
  if (!store) {
    throw new Error('useStore must be used within a StoreProvider.');
  }
  return store;
};

export {StoreProvider, useStore, creationStore};
// export
